<script setup>
/**
 * @fileoverview Step 1 — pick one of the patterns from `data/patterns.json`.
 *
 * Supports a name search. Selecting a pattern previews it but keeps this step
 * open, so shoppers can browse patterns before choosing colors.
 *
 * @component
 */
import { ref, computed } from 'vue';
import { useMosaicStore } from '../stores/mosaicStore';
import { trackPatternSelection } from '../utils/trackingService';
import MosaicDrawing from './MosaicDrawing.vue';

const store = useMosaicStore();

const search = ref('');

const filteredPatterns = computed(() => {
    const q = search.value.trim().toLowerCase();
    return q ? store.patterns.filter((p) => p.name.toLowerCase().includes(q)) : store.patterns;
});

function select(pattern) {
    if (store.selectedPatternId === pattern.id) return;
    store.selectPattern(pattern.id);
    trackPatternSelection(pattern);
}
</script>

<template>
    <div class="p-4 flex flex-col gap-3 min-h-0">
        <div class="flex flex-col gap-2">
            <input
                v-model="search"
                type="search"
                placeholder="Search patterns by name..."
                aria-label="Search patterns by name"
                class="w-full border border-engobe px-3 py-2 text-sm"
            />
            <p class="text-xs text-dryline mb-0" aria-live="polite">
                {{ filteredPatterns.length }} patterns
            </p>
        </div>

        <div
            class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-3 gap-2 md:gap-3 p-2 md:p-3 max-h-[48vh] overflow-auto bg-gray5new"
            role="group"
            aria-label="Pattern options"
        >
            <button
                v-for="p in filteredPatterns"
                :key="p.id"
                type="button"
                class="flex flex-col items-center gap-1 p-2 border bg-white shadow-sm hover:shadow hover:border-dryline transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                :class="store.selectedPatternId === p.id ? 'border-dryline' : 'border-transparent'"
                :aria-pressed="store.selectedPatternId === p.id ? 'true' : 'false'"
                :aria-label="`Select pattern ${p.name}`"
                @click="select(p)"
            >
                <MosaicDrawing
                    aria-hidden="true"
                    focusable="false"
                    :pattern="p"
                    :grout-hex="store.groutColor?.hex"
                    class="w-full aspect-square"
                />
                <span class="text-[11px] md:text-xs text-black text-center leading-tight">
                    {{ p.name }}
                </span>
                <span class="text-[10px] text-dryline text-center leading-tight">
                    {{ p.width }}×{{ p.height }}" · {{ p.tileCount }} tiles
                </span>
            </button>
            <p
                v-if="filteredPatterns.length === 0"
                class="col-span-full text-sm text-dryline text-center py-6 mb-0"
            >
                No patterns match “{{ search }}”.
            </p>
        </div>
    </div>
</template>
